// [S-220] Certification queue — focused surface for templates sitting in
// `under_review`. Per-row: Open (deep-link to /library/:id), Certify
// (one-click → TemplateService.certify transitions under_review →
// re_certified), Deprecate (existing modal with successor picker).
//
// This is a re-certification workflow specifically — drafts → certified
// stays in TemplateAuthor / TemplateEdit. The queue is the operational
// surface for "review cycle".
//
// Role gate at the route layer (RequireRole minRole=tenant_admin). The
// server-side TemplateService.certify re-checks per-layer role.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  LoadingState,
  PageHead,
  Pagination,
  usePagination,
} from "../components";
import { useSession } from "../auth/AuthProvider";
import {
  useAdminTemplates,
  useRecordPeerReview,
  type CertifiedTemplateRow,
} from "../data/templatesQuery";
import { useCertifyL1Template } from "../data/l1TemplatesQuery";
import { useCertifyL2Template } from "../data/l2TemplatesQuery";
import {
  AssignPeerReviewerModal,
  DeprecateTemplateModal,
} from "../components/templates/TemplateLifecycleModals";
import { useActorLabels } from "../data/identitiesQuery";

// [Review Engine V2] The clause-taxonomy coverage of whatever was just
// certified, measured server-side DURING certify() and returned on the response.
// Shown only when something went unnamed, and only as a warning — coverage never
// blocks a certification. A genuinely novel clause is exactly where a human
// should look, not be stopped; a hard gate would push whoever is certifying into
// inventing a clause_type to get past it, manufacturing the near-duplicates the
// controlled taxonomy exists to prevent.
function CoverageWarning({
  certified,
}: {
  certified: CertifiedTemplateRow | undefined;
}): JSX.Element {
  const coverage = certified?.taxonomyCoverage;
  // Null coverage means never measured (the classifier was unreachable, or the
  // engine flag is off) — NOT "everything was named". Say nothing rather than
  // imply a clean result.
  if (certified === undefined || coverage == null) return <></>;
  if (coverage.uncovered.length === 0) return <></>;
  return (
    <Alert
      label={`${coverage.uncovered.length} clause${coverage.uncovered.length === 1 ? "" : "s"} the taxonomy can't name`}
      tone="warning"
    >
      <>
        <strong>{templateDisplayName(certified)}</strong> certified with{" "}
        {coverage.typedClauses} of {coverage.totalClauses} clauses typed. A clause
        no clause type names is dropped from the standard the review engine aligns
        against, so a draft can never be flagged for omitting it:{" "}
        {coverage.uncovered.map((c) => c.heading).join(", ")}.
      </>
    </Alert>
  );
}

// The name a reviewer recognizes: the uploaded title when set, else a
// readable deal/guild label (legacy/seeded rows have no title). Never the
// opaque id.
function templateDisplayName(t: CertifiedTemplateRow): string {
  const title = t.title?.trim();
  if (title !== undefined && title.length > 0) return title;
  return `${t.dealType.replace(/_/g, " ")} · ${t.guild}`;
}

export function CertificationQueue(): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";

  const query = useAdminTemplates(tenantId, {});
  const allRows = query.data ?? [];
  const rows = allRows.filter((r) => r.lifecycleState === "under_review");
  const [deprecating, setDeprecating] =
    useState<CertifiedTemplateRow | null>(null);
  // [S-221 follow-up] Assign-peer-reviewer modal target.
  const [assigningPeer, setAssigningPeer] =
    useState<CertifiedTemplateRow | null>(null);
  const certifyL1 = useCertifyL1Template();
  const certifyL2 = useCertifyL2Template(tenantId);
  // [S-221 follow-up] Record peer review — the assigned peer reviewer
  // (callerUid === peerReviewerUid) clicks "I've reviewed it" to stamp
  // peerReviewedAt. Visibility gated below.
  const recordPeer = useRecordPeerReview();
  const ownerLabels = useActorLabels(rows.map((r) => r.submittedBy), tenantId);
  const pager = usePagination(rows, 25);

  const handleCertify = (t: CertifiedTemplateRow): void => {
    const effectiveFrom = new Date().toISOString();
    if (t.layerSource === "L1") {
      certifyL1.mutate({ templateId: t.id, effectiveFrom });
    } else {
      certifyL2.mutate({ templateId: t.id, effectiveFrom });
    }
  };

  return (
    <>
      <PageHead
        eyebrow="Platform · certification queue"
        title={
          <>
            Re-certify <em>under-review</em> templates.
          </>
        }
        sub={
          query.isLoading
            ? "Loading…"
            : `${rows.length} template${rows.length === 1 ? "" : "s"} awaiting re-certification.`
        }
        cta={
          <Link to="/template-management" className="btn btn--g btn--s">
            Back to management
          </Link>
        }
      />

      {query.isError && (
        <Alert label="Couldn't load" tone="error">
          {query.error?.message ?? "unknown error"}
        </Alert>
      )}

      {(query.isLoading || (query.isFetching && rows.length === 0)) && (
        <LoadingState label="Loading certification queue…" />
      )}

      {!query.isLoading && !query.isFetching && !query.isError && rows.length === 0 && (
        <div className="ingest__state" data-testid="certq-empty">
          Nothing in <code>under_review</code>. Flag a template from
          Template Management to move it here.
        </div>
      )}

      {!query.isLoading && !query.isError && rows.length > 0 && (
        <div className="ingest__tbl" role="table" aria-label="Re-certification queue">
          <div className="ingest__row ingest__row--head" role="row">
            <div role="columnheader" className="ingest__cell ingest__cell--id">Template</div>
            <div role="columnheader" className="ingest__cell">Layer</div>
            <div role="columnheader" className="ingest__cell">Deal · Guild</div>
            <div role="columnheader" className="ingest__cell">Last modified</div>
            <div role="columnheader" className="ingest__cell">By</div>
            <div role="columnheader" className="ingest__cell ingest__cell--act">Actions</div>
          </div>
          {pager.pageItems.map((t) => {
            const isCertifying =
              (t.layerSource === "L1" &&
                certifyL1.isPending &&
                certifyL1.variables?.templateId === t.id) ||
              (t.layerSource === "L2" &&
                certifyL2.isPending &&
                certifyL2.variables?.templateId === t.id);
            return (
              <div
                className="ingest__row"
                key={t.id}
                role="row"
                data-testid={`certq-row-${t.id}`}
              >
                <div role="cell" className="ingest__cell ingest__cell--id">
                  {templateDisplayName(t)}
                </div>
                <div role="cell" className="ingest__cell">
                  <span
                    className={
                      t.layerSource === "L2"
                        ? "tplmgmt__layer tplmgmt__layer--l2"
                        : "tplmgmt__layer tplmgmt__layer--l1"
                    }
                  >
                    {t.layerSource}
                  </span>
                </div>
                <div role="cell" className="ingest__cell">
                  {t.dealType.replace(/_/g, " ")} · {t.guild}
                </div>
                <div role="cell" className="ingest__cell">
                  {new Date(t.effectiveFrom).toLocaleDateString()}
                </div>
                <div role="cell" className="ingest__cell">
                  {ownerLabels?.get(t.submittedBy) ?? "—"}
                </div>
                <div role="cell" className="ingest__cell ingest__cell--act">
                  <Button
                    variant="ghost"
                    className="btn--xs"
                    onClick={() => navigate(`/library/${encodeURIComponent(t.id)}`)}
                  >
                    Open
                  </Button>
                  {/* [S-221 follow-up] Peer-reviewer assignment +
                      record. We surface ONE of these two buttons per
                      row based on caller identity:
                        - lead attestor (anyone not the peerReviewer):
                          "Assign peer" / "Re-assign peer"
                        - the assigned peer reviewer themselves:
                          "Record review" (until reviewed) */}
                  {session !== null &&
                    session.uid === t.peerReviewerUid &&
                    (t.peerReviewedAt === null ||
                      t.peerReviewedAt === undefined) && (
                      <Button
                        variant="ghost"
                        className="btn--xs"
                        onClick={() =>
                          recordPeer.mutate({
                            templateId: t.id,
                            tenantId: t.tenantId,
                          })
                        }
                        disabled={
                          recordPeer.isPending &&
                          recordPeer.variables?.templateId === t.id
                        }
                        data-testid={`certq-record-peer-${t.id}`}
                      >
                        {recordPeer.isPending &&
                        recordPeer.variables?.templateId === t.id
                          ? "Recording…"
                          : "Record review"}
                      </Button>
                    )}
                  {session !== null && session.uid !== t.peerReviewerUid && (
                    <Button
                      variant="ghost"
                      className="btn--xs"
                      onClick={() => setAssigningPeer(t)}
                      data-testid={`certq-assign-peer-${t.id}`}
                    >
                      {t.peerReviewerUid != null && t.peerReviewerUid.length > 0
                        ? "Re-assign peer"
                        : "Assign peer"}
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    className="btn--xs"
                    onClick={() => handleCertify(t)}
                    disabled={isCertifying}
                    data-testid={`certq-certify-${t.id}`}
                  >
                    {isCertifying ? "Re-certifying…" : "Re-certify"}
                  </Button>
                  <Button
                    variant="ghost"
                    className="btn--xs"
                    onClick={() => setDeprecating(t)}
                    data-testid={`certq-deprecate-${t.id}`}
                  >
                    Deprecate
                  </Button>
                </div>
              </div>
            );
          })}
          {(certifyL1.isError || certifyL2.isError) && (
            <Alert label="Certify failed" tone="error">
              {certifyL1.error?.message ??
                certifyL2.error?.message ??
                "unknown error"}
            </Alert>
          )}
          <CoverageWarning certified={certifyL1.data ?? certifyL2.data} />
        </div>
      )}

      {rows.length > 0 && (
        <Pagination
          page={pager.page}
          pageCount={pager.pageCount}
          onPageChange={pager.setPage}
          total={pager.total}
          rangeStart={pager.rangeStart}
          rangeEnd={pager.rangeEnd}
          itemLabel="template"
        />
      )}

      <DeprecateTemplateModal
        template={deprecating}
        siblings={allRows}
        onClose={() => setDeprecating(null)}
      />
      <AssignPeerReviewerModal
        template={assigningPeer}
        callerUid={session?.uid ?? ""}
        onClose={() => setAssigningPeer(null)}
      />
      {recordPeer.isError && (
        <Alert label="Couldn't record peer review" tone="error">
          {recordPeer.error?.message ?? "unknown error"}
        </Alert>
      )}
    </>
  );
}
